import cloneDeep from 'lodash.clonedeep';

export function useSurfaceEditorElementFactory() {
  const surfaceEditorUtils = useSurfaceEditorUtils();

  function getTextPresets(surface, {dynamicTextData} = {}) {
    if (!surface?.width || !surface?.height) {
      return {
        defaultPresets: [],
        dynamicPresets: []
      };
    }

    const defaultPresets = [
      {
        id: 'heading',
        label: 'Add a heading',
        conf: {
          type: SURFACE_EDITOR_ELEMENT_TYPE.text,
          ...surfaceEditorUtils.centerElementInSurface({
            surface,
            element: {
              width: surface.width * 0.45,
              height: 200,
            },
            minDistanceFromEdge: 200
          }),
          draggable: true,
          resizable: true,
          opacity: 1,
          rotation: 0,
          content: {
            text: 'Heading Text',
            scale: 1,
            font_size: 22,
            font_size_multiplier: 0.06,
            line_height: 1.2,
            letter_spacing: 0,
            font_family: 'Poppins',
            color: '#333333',
            align: 'center',
            bold:  true,
            italic: false,
            underline:  false,
            strikethrough: false
          }
        }
      },
      {
        id: 'subheading',
        label: 'Add a subheading',
        conf: {
          type: SURFACE_EDITOR_ELEMENT_TYPE.text,
          ...surfaceEditorUtils.centerElementInSurface({
            surface,
            element: {
              width: surface.width * 0.45,
              height: 200,
            },
            minDistanceFromEdge: 200
          }),
          draggable: true,
          resizable: true,
          opacity: 1,
          rotation: 0,
          content: {
            text: 'Subheading Text',
            scale: 1,
            font_size: 16,
            font_size_multiplier: 0.05,
            line_height: 1.34,
            letter_spacing: 0,
            font_family: 'Poppins',
            color: '#333333',
            align: 'center',
            bold:  true,
            italic: false,
            underline:  false,
            strikethrough: false
          }
        }
      },
      {
        id: 'body',
        label: 'Add body text',
        conf: {
          type: SURFACE_EDITOR_ELEMENT_TYPE.text,
          ...surfaceEditorUtils.centerElementInSurface({
            surface,
            element: {
              width: surface.width * 0.3,
              height: 200,
            },
            minDistanceFromEdge: 200
          }),
          draggable: true,
          resizable: true,
          opacity: 1,
          rotation: 0,
          content: {
            text: 'Body Text',
            scale: 1,
            font_size: 12,
            font_size_multiplier: 0.03,
            line_height: 1.5,
            letter_spacing: 0,
            font_family: 'Poppins',
            color: '#333333',
            align: 'center',
            bold:  false,
            italic: false,
            underline:  false,
            strikethrough: false
          }
        }
      }
    ];

    const dynamicPresets = [];

    //todo: these seem to belong more to a specific use case of the surface editor than the editor itself - consider refactoring
    const headingPreset = defaultPresets.find(text => text.id === 'heading');
    if (dynamicTextData?.name != null && headingPreset?.conf) {
      const namePreset = cloneDeep(headingPreset);
      namePreset.id = 'heading-dynamic-name';
      namePreset.label = String(dynamicTextData.name);
      namePreset.conf.content.template = '{{name}}';
      namePreset.conf.content.text = String(dynamicTextData.name);
      dynamicPresets.push(namePreset);
    }

    const subheadingPreset = defaultPresets.find(text => text.id === 'subheading');
    if (dynamicTextData?.date != null && subheadingPreset?.conf) {
      const datePreset = cloneDeep(subheadingPreset);
      datePreset.id = 'subheading-dynamic-date';
      datePreset.label = String(dynamicTextData.date);
      datePreset.conf.content.template = '{{date}}';
      datePreset.conf.content.text = String(dynamicTextData.date);
      dynamicPresets.push(datePreset);
    }
    if (dynamicTextData?.ownerName != null && subheadingPreset?.conf) {
      const ownerNamePreset = cloneDeep(subheadingPreset);
      ownerNamePreset.id = 'subheading-dynamic-owner-name';
      ownerNamePreset.label = String(dynamicTextData.ownerName);
      ownerNamePreset.conf.content.template = '{{ownerName}}';
      ownerNamePreset.conf.content.text = String(dynamicTextData.ownerName);
      dynamicPresets.push(ownerNamePreset);
    }

    const bodyPreset = defaultPresets.find(text => text.id === 'body');
    if (dynamicTextData?.description != null && bodyPreset?.conf) {
      const descriptionPreset = cloneDeep(bodyPreset);
      descriptionPreset.id = 'body-dynamic-description';
      descriptionPreset.label = String(dynamicTextData.description);
      descriptionPreset.conf.content.template = '{{description}}';
      descriptionPreset.conf.content.text = String(dynamicTextData.description);
      dynamicPresets.push(descriptionPreset);
    }
    if (dynamicTextData?.clipLength != null && bodyPreset?.conf) {
      const clipLengthPreset = cloneDeep(bodyPreset);
      clipLengthPreset.id = 'body-dynamic-clip-length';
      clipLengthPreset.label = String(dynamicTextData.clipLength);
      clipLengthPreset.conf.content.template = '{{clipLength}}';
      clipLengthPreset.conf.content.text = String(dynamicTextData.clipLength);
      dynamicPresets.push(clipLengthPreset);
    }

    return {
      defaultPresets,
      dynamicPresets
    };
  }

  function buildTextElement({preset, surface, position}) {
    if (!preset?.conf || !surface?.width || !surface?.height) {
      return null;
    }

    const element = Object.assign({}, preset.conf, {
      content: {
        ...preset.conf.content,
        font_size: Math.round(Math.min(surface.width, surface.height) * preset.conf.content.font_size_multiplier),
        font_size_multiplier: undefined
      }
    });

    if (position) {
      element.x = position.x;
      element.y = position.y;
    }

    return element;
  }

  function getShapePresets(surface) {
    if (!surface?.width || !surface?.height) {
      return [];
    }

    return [
      {
        id: 'rectangle',
        icon: 'i-ri-rectangle-fill',
        conf: {
          type: SURFACE_EDITOR_ELEMENT_TYPE.shape,
          ...surfaceEditorUtils.centerElementInSurface({
            surface,
            element: (() => {
              const minDim = Math.min(surface.width, surface.height);

              return { //16:9 ratio
                width: minDim * 0.32,
                height: minDim * 0.18,
              }
            })()
          }),
          opacity: 1,
          rotation:  0,
          draggable: true,
          resizable: true,
          content: {
            shape_type: SURFACE_EDITOR_SHAPES.rectangle,
            fill: {
              type: SURFACE_EDITOR_FILL_TYPES.solid,
              color: '#222222'
            },
            stroke_width: 0,
            stroke_type: SURFACE_EDITOR_STROKE_STYLES.none,
            rounding: 0
          }
        }
      },
      {
        id: 'circle',
        icon: 'i-ri-circle-fill',
        conf: {
          type: SURFACE_EDITOR_ELEMENT_TYPE.shape,
          ...surfaceEditorUtils.centerElementInSurface({
            surface,
            element: {
              width: surface.width * 0.25,
              height: surface.width * 0.25,
            }
          }),
          opacity: 1,
          rotation:  0,
          draggable: true,
          resizable: true,
          content: {
            shape_type: SURFACE_EDITOR_SHAPES.circle,
            fill: {
              type: SURFACE_EDITOR_FILL_TYPES.solid,
              color: '#222222'
            },
            stroke_width: 0,
            stroke_type: SURFACE_EDITOR_STROKE_STYLES.none,
            rounding: 100
          }
        }
      }
    ];
  }

  function buildShapeElement({preset, position}) {
    if (!preset?.conf) {
      return null;
    }

    const element = Object.assign({}, preset.conf);

    if (position) {
      element.x = position.x;
      element.y = position.y;
    }

    return element;
  }

  function buildPhotoElement({file, surface}) {
    if (!file?.images?.original || !surface?.width || !surface?.height) {
      return null;
    }

    const position = surfaceEditorUtils.centerElementInSurface({
      surface,
      element: file,
      minDistanceFromEdge: Math.min(surface.width, surface.height) * 0.2
    });

    return {
      type: SURFACE_EDITOR_ELEMENT_TYPE.image,
      x: position.x,
      y: position.y,
      width: position.width,
      height: position.height,
      opacity: 1,
      rotation: 0,
      draggable: true,
      resizable: true,
      content: {
        url: file.images?.original,
        working_url: file.images?.preview,
        rounding: 0,
        forever_file_id: file.id,
        image_width: file.width,
        image_height: file.height,
        scale: position.scale,
        offset_x: 0,
        offset_y: 0,
        image_rotation: 0
      }
    };
  }

  function buildPhotoContentForElement({element, file}) {
    if (!element?.width || !element?.height || !file?.width || !file?.height) {
      return null;
    }

    const scale = Math.max(element.width / file.width, element.height / file.height);
    const scaledWidth = file.width * scale;
    const scaledHeight = file.height * scale;

    return {
      url: file.images?.original,
      working_url: file.images?.preview,
      rounding: element.content?.rounding ?? 0,
      forever_file_id: file.id,
      image_width: file.width,
      image_height: file.height,
      scale,
      offset_x: Math.round((element.width - scaledWidth) / 2),
      offset_y: Math.round((element.height - scaledHeight) / 2),
      image_rotation: 0
    };
  }

  return {
    getTextPresets,
    buildTextElement,
    getShapePresets,
    buildShapeElement,
    buildPhotoElement,
    buildPhotoContentForElement
  };
}
